import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
    Button,
    Box,
    CircularProgress,
    Grid,
    List,
    Pagination,
    styled,
    TextField,
    Tooltip,
    Typography,
    FormControl
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import TripCard from "./TripCard";
import { useGetTripsQuery } from "../../services/routing";
import { tripListToRows } from "../trips/tripUtils";
import { closeDrawer, openTripInput } from "../../features/Drawer/drawerSlice";
import MobileNavBar from "./MobileNavBar";
import { ZIndexLevels } from "../risk/geoUtils";
import { setSelectionState, setWilmaState, useRiskViewerState } from "../../features/riskViewer/riskViewerSlice";
import CloseIcon from "@mui/icons-material/Close";
import { faCircleXmark } from "@fortawesome/sharp-duotone-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIsMobile } from "../../features/pageWidth/pageWidthSlice";
import { AnyAction } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { ACTIVE_STATUS, ARCHIVED_STATUS, CLEAR_MAP, CLOSE_DRAWER, ERROR_FETCHING_TRIPS } from "../UserVisibleStrings";
import { Trip } from "../../generated/gpb/api/route";
import { setSelectedTime } from "../../features/surface/surfaceSlice";
import { defaultWaypoint } from "../trips/form/TripFormTypeConversions";
import { setOpenroadsVisibilityToFalse } from "../../features/mapDisplay/mapDisplaySlice";
import Toggle from "../trips/form/Toggle";

const DrawerHeader = styled( "div" )( ( { theme } ) => ( {
    position: "sticky",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing( 0, 1 ),
    ...theme.mixins.toolbar,
} ) );

const PAGE_COUNT = 5;
const handleInputOpen = ( riskViewerState: string, dispatch: Dispatch<AnyAction> ) =>
{
    dispatch( setSelectionState( { waypoints: [ defaultWaypoint, defaultWaypoint ], homeViewState: undefined } ) );
    dispatch( openTripInput() );
};

export default function TripLibraryDrawer( )
{
    const [ searchTerm, setSearchTerm ] = useState( "" );
    const [ page, setPage ] = useState( 1 );

    const handlePageChange = ( event: React.ChangeEvent<unknown>, value: number ) =>
    {
        setPage( value );
    };
    const handleSearchTerm = ( e: { target: { value: SetStateAction<string> } } ): void =>
    {
        setSearchTerm( e.target.value.toString().toLowerCase() );
    };

    const [ archiveTripSelection, setArchiveTripSelection ] = React.useState<boolean>( false );
    const { data: allData, isFetching: isTripFetching, isError, refetch, isUninitialized } = useGetTripsQuery(
        {
            index: ( page - 1 ) * PAGE_COUNT,
            count: PAGE_COUNT,
            sorting: [],
            filter: [],
            archiveFilter: searchTerm.length === 0 ? archiveTripSelection/* !== ACTIVE_STATUS*/ : undefined, // if user is searching, search both past and present trips
            searchTerm: searchTerm
        },
        {
            // when a trip is processing on the page we are currently viewing,
            // requery the backend more frequently to improve responsiveness.
            // a more targeted query (e.g. adding a new endpoint) could achieve
            // the same effect and would be cleaner, but offers little performance
            // benefit.
            pollingInterval: 60000,
        }
    );
    useEffect( () =>
    {
        if ( !isUninitialized )
        {
            refetch().catch( err => console.error( err ) );
        }
    }, [ searchTerm, archiveTripSelection ] );

    const rawTripData = allData?.trip;
    const total: number | undefined = allData?.totalUnpaginatedTrips;
    const routeRequests: Array<Trip> | undefined = React.useMemo( () =>
    {
        return tripListToRows( allData );
    }, [ rawTripData ] );


    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const riskViewerStateKind = useRiskViewerState().kind;
    const NavBarProps = {
        onClose: () => dispatch( closeDrawer() ),
    };

    // Only show the map clear button in the top right of the drawer if we're currently displaying a route on the map
    // in desktop mode (mobile mode has a button at the bottom)
    const showClearMapButton = !isMobile && riskViewerStateKind === "tripState";
    // Only show the close drawer button at the top of the drawer for mobile
    // as the desktop view has the button to the right of the drawer (it's too crowded to reuse that button in mobile)
    const showCloseDrawerButton = isMobile;

    return (
        <div style = {{
            display: "flex",
            flexDirection: "column",
            height: "100dvh",
            overflow: "hidden" // the overflow applies to this component but not to the sub components
        }}
        >
            <DrawerHeader
                sx = {{
                    paddingRight: 2,
                    paddingLeft: 2,
                    paddingTop: "8px",
                    position: "sticky",
                    top: 0,
                    zIndex: ZIndexLevels.TopUIElement,
                    background: "white",
                    borderBottom: "3px black",
                    minHeight: "64px",
                }}
            >
                <Grid container spacing = {2} sx = {{ height: "100%", justifyContent: "center", minHeight: "65px" }} alignItems = "center">
                    <Grid item xs = {3} sx = {{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Button
                            onClick = {() => handleInputOpen( riskViewerStateKind, dispatch )}
                            variant = "contained"
                            sx = {{
                                maxHeight: "50px"
                            }}
                        >
                            <AddIcon/>
                        </Button>
                    </Grid>
                    <Grid item xs = {showClearMapButton || showCloseDrawerButton ? 7 : 9} sx = {{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <TextField
                            type = "text"
                            placeholder = "Search all trips..."
                            fullWidth
                            onChange = {handleSearchTerm}
                            sx = {{
                                height: "40px",
                                "& .MuiInputBase-root": {
                                    height: "100%"
                                },
                                "& .MuiOutlinedInput-input": {
                                    padding: "10px"
                                }
                            }}
                        />
                    </Grid>
                    {showClearMapButton && (
                        <Grid item xs = {2} sx = {{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Tooltip title = {CLEAR_MAP}>

                                <Button onClick = {() =>
                                {
                                    dispatch( setWilmaState( ) );
                                    dispatch( setSelectedTime( Date.now() ) );
                                    dispatch( closeDrawer() );
                                    dispatch( setOpenroadsVisibilityToFalse() );
                                }}
                                >
                                    <FontAwesomeIcon icon = {faCircleXmark} size = "2x" fixedWidth/>
                                </Button>
                            </Tooltip>
                        </Grid>
                    )}
                    {showCloseDrawerButton && (
                        <Grid item xs = {2} sx = {{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Tooltip title = {CLOSE_DRAWER}>
                                <Button onClick = {() => dispatch( closeDrawer() )}>
                                    <CloseIcon/>
                                </Button>
                            </Tooltip>
                        </Grid>
                    )}
                    {searchTerm.length === 0 && (
                        <Grid item xs = {12}>

                            <Box sx = {{ minWidth: 120, paddingBottom: "8px" }}>
                                <FormControl fullWidth size = "small">
                                    <Toggle value = {archiveTripSelection}
                                        setValue = {setArchiveTripSelection}
                                        labelTrue = {ARCHIVED_STATUS}
                                        labelFalse = {ACTIVE_STATUS}
                                        tooltipFalse = {"Old trips you've archived"}
                                    />
                                </FormControl>
                            </Box>
                        </Grid>
                    )}
                    {isError && (
                        <Box sx = {{ width: "100%", paddingTop: 1 }}>
                            <Typography color = "error" variant = "body2" align = "center">
                                {ERROR_FETCHING_TRIPS}
                            </Typography>
                        </Box>
                    )}
                </Grid>
            </DrawerHeader>
            <div style = {{ flex: "1", overflow: "auto" }}>
                <List
                    sx = {{
                        paddingTop: "0px",
                        paddingBottom: "0px",
                    }}
                >
                    {isTripFetching && routeRequests.length === 0 && <CircularProgress/>}
                    {routeRequests.length > 0 && routeRequests
                        .filter( ( trip ) => trip !== undefined )
                        .map( ( trip, indexTrip ) => (
                            <TripCard
                                key = {`trip${indexTrip}`}
                                trip = {trip}
                                resetToPage = {handlePageChange}
                                refetch = {refetch}
                            /> ) )}
                    { !isTripFetching && routeRequests.length === 0 && !isError && <span>No results found. </span>}
                    {total !== undefined && total > 1 && (
                        <Pagination
                            count = {Math.ceil( total / PAGE_COUNT )}
                            page = {page}
                            onChange = {handlePageChange}
                            color = "primary"
                        /> )}
                </List>
            </div>
            {isMobile && /* Dumb, but we have to include a second toolbar in order for the MobileNavBar to not cover up the pagination controls*/ (
                <MobileNavBar {...NavBarProps}/>
            )}
        </div>
    );
}
